<script setup lang="ts">
import { computed, ref } from 'vue'
import { EventDB } from '@/enitites/event/model/types'
import IconLike from '@/shared/icons/IconLike.vue'
import IconMinusCircle from '@/shared/icons/IconMinusCircle.vue'
import IconPlusCircle from '@/shared/icons/IconPlusCircle.vue'
import { EVENT_STAR_CONFIG } from '../constants'

interface Props {
  event?: EventDB
}

const props = withDefaults(defineProps<Props>(), {
  event: undefined,
})

const isOpenRating = ref(false)

const RATING_CATEGORIES = [
  { key: 'timeliness', label: 'Timeliness', value: props.event?.totalTimeliness },
  { key: 'communication', label: 'Communication', value: props.event?.totalCommunication },
  { key: 'venue', label: 'Venue', value: props.event?.totalVenue },
  { key: 'vibe', label: 'Vibe', value: props.event?.totalVibe },
  { key: 'musicSound', label: 'Music / Sound', value: props.event?.totalMusicSound },
] as const

const displayReviewCount = computed(() => `${props.event?.countFeedbacks} reviews`)

const handleClickTopButton = () => {
  isOpenRating.value = !isOpenRating.value
}

const displayOverallRating = computed(() => `${props.event?.totalAverageRating}/5`)
</script>

<template>
  <div
    class="feedback-wrapper"
    :style="{
      display: 'block',
    }"
  >
    <div
      class="feedback-container feedback-container--opacity"
      style="opacity: 0"
    >
      <button class="feedback-item">
        <div class="avg-rating">
          <div class="rating-header">
            <v-rating
              v-bind="EVENT_STAR_CONFIG"
              :value="event.totalAverageRating"
              color="white"
              background-color="white"
              readonly
            />
            <div class="rating-text">{{ displayOverallRating }}</div>
          </div>

          <div class="review-count">{{ displayReviewCount }}</div>
        </div>
      </button>

      <div class="feedback-divider" />

      <button class="feedback-item">
        <IconLike :fill="true" />
        <span>{{ event.countLikes }}</span>
      </button>
    </div>
    <div
      class="feedback-container feedback-container--active"
      :class="{ 'feedback-container--expanded': isOpenRating }"
    >
      <button
        class="feedback-item"
        @click="handleClickTopButton"
      >
        <div class="avg-rating">
          <div class="rating-header">
            <v-rating
              v-bind="EVENT_STAR_CONFIG"
              :value="event.totalAverageRating"
              color="white"
              background-color="white"
              readonly
            />
            <div class="rating-text">{{ displayOverallRating }}</div>
          </div>

          <div class="review-count">{{ displayReviewCount }}</div>

          <div
            class="rating-categories"
            :class="{ 'rating-categories--open': isOpenRating }"
          >
            <div
              v-for="category in RATING_CATEGORIES"
              :key="category.key"
              class="rating-item"
            >
              <v-rating
                v-bind="EVENT_STAR_CONFIG"
                :value="category.value"
                color="white"
                background-color="white"
                readonly
              />
              <div class="category-label">{{ category.label }}</div>
            </div>
          </div>

          <div class="icon">
            <IconMinusCircle v-if="isOpenRating" />
            <IconPlusCircle v-else />
          </div>
        </div>
      </button>

      <div class="feedback-divider" />

      <div class="feedback-item">
        <IconLike :fill="true" />
        <span>{{ event.countLikes }}</span>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
@import '@/assets/style/mixins';

.feedback-wrapper {
  position: relative;
  display: block;
}

.feedback-container {
  position: absolute;
  top: 0;
  right: 0;
  left: 0;
  z-index: 2;
  overflow: hidden;
  background: #000c;
  border: 1px solid rgb(255 255 255 / 30%);
  border-radius: 8px;
  box-shadow: 0 1px 4px rgb(0 0 0 / 20%);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);

  @media (max-width: 920px) {
    position: relative;
  }

  &--active {
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  }

  &--opacity {
    position: relative;
    display: block;

    @media (max-width: 920px) {
      display: none;
    }
  }
}

.feedback-item {
  @include font-h5;

  position: relative;
  display: flex;
  gap: 8px;
  align-items: center;
  justify-content: flex-start;
  width: 100%;
  padding: 20px;
  color: white;
  background: none;
  border: none;
  transition: background-color 0.2s ease;

  &:hover {
    background: rgb(255 255 255 / 5%);
  }
}

.feedback-divider {
  width: 100%;
  height: 1px;
  background: rgb(255 255 255 / 30%);
}

.feedback {
  display: flex;
  flex-direction: column;
  gap: 4px;
  align-items: start;
  justify-content: start;
}

.avg-rating {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  width: 100%;
  padding-right: 24px;
}

.rating-header {
  @include font-body1;

  display: flex;
  gap: 10px;
  align-items: center;
  color: white;
  font-weight: 600;
}

.rating-text {
  font-size: inherit;
}

.review-count {
  @include font-caption1;

  margin-top: 4px;
  color: rgb(255 255 255 / 80%);
}

.rating-categories {
  display: flex;
  flex-direction: column;
  gap: 20px;
  width: 100%;
  max-height: 0;
  margin-top: 0;
  overflow: hidden;
  transform: translateY(-20px);
  opacity: 0;
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);

  &--open {
    max-height: 300px;
    margin-top: 20px;
    transform: translateY(0);
    opacity: 1;
  }
}

.rating-item {
  display: flex;
  gap: 10px;
  align-items: center;

  @include font-body2;

  color: #eee;
  transform: translateX(-10px);
  opacity: 0;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);

  .rating-categories--open & {
    transform: translateX(0);
    opacity: 1;

    // Задержка анимации для каждого элемента
    @for $i from 1 through 5 {
      &:nth-child(#{$i}) {
        transition-delay: #{($i - 1) * 0.1}s;
      }
    }
  }
}

.category-label {
  flex: 1;
  text-align: left;
}

.icon {
  position: absolute;
  top: 10px;
  right: 10px;
  z-index: 1000;
  transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);

  &--rotated {
    transform: rotate(180deg);
  }
}

.star {
  gap: 5px;

  :deep(button) {
    padding: 0;
  }
}

.arrow-icon {
  margin-left: 4px;
  transform: rotate(180deg);
}
</style>
